import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ShareProjectModal from '../../components/ShareProjectModal';
import { useProjects } from './hooks/useProjects';
import { useAdmin } from '../../hooks/useAdmin';
import WelcomeHeader from './components/WelcomeHeader';
import QuickActions from './components/QuickActions';
import ProjectsList from './components/ProjectsList';
import AddProjectModal from './components/AddProjectModal';

const WelcomePage: React.FC = () => {
  const {
    projects,
    isLoading,
    error,
    addProject,
    deleteProject,
    runProjectAnalysis,
    refreshProjects
  } = useProjects();
  
  // Check if user is admin
  const { isAdmin } = useAdmin();

  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [shareProjectId, setShareProjectId] = useState<string | null>(null);
  const [shareProjectName, setShareProjectName] = useState<string>('');

  // Handlers for action buttons
  const handleFindLocation = () => {
    // Open the add project modal
    setShowAddProjectModal(true);
    console.log('Find a Business Location clicked');
  };

  const handleAddProject = async (projectData: { 
    name: string; 
    location: string; 
    coordinates?: { latitude: number; longitude: number };
    addressComponents?: any;
  }) => {
    const success = await addProject(projectData);
    if (success) {
      setShowAddProjectModal(false);
    }
  };
  
  const handleCloseModal = () => {
    setShowAddProjectModal(false);
  };

  const handleAnalyzeCompetition = () => {
    console.log('Analyze Competition clicked');
  };

  const handleRunMarketReport = () => {
    console.log('Run Market Report clicked');
  };

  const handleMonitorSite = () => {
    console.log('Monitor Site Activity clicked');
  };

  const handleGetAISuggestions = () => {
    console.log('Get AI Suggestions clicked');
  };

  // Project action handlers
  const handleViewAnalysis = async (projectId: string) => {
    console.log(`Viewing analysis for project ${projectId}`);
    // In a real implementation, this would navigate to a project analysis page
    // or fetch and display the analysis data
  };

  const handleCloseShareModal = () => {
    setShareProjectId(null);
    setShareProjectName('');
  };

  return (
    <div className="welcome-page" style={{ 
      backgroundColor: '#f0f0f0', 
      minHeight: '100vh', 
      padding: '1rem'
    }}>
      <div style={{ 
        maxWidth: '1400px', 
        margin: '0 auto',
        width: '100%'
      }}>
        {/* Welcome Message and Branding */}
        <WelcomeHeader />
        
        {/* Admin Panel Link - Only visible to admin users */}
        {isAdmin && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Admin Tools</h2>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/admin/industry-insights"
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
              >
                <span className="material-icons">📚</span>
                <span>Industry Insights Manager</span>
              </Link>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <QuickActions
          onFindLocation={handleFindLocation}
          onAnalyzeCompetition={handleAnalyzeCompetition}
          onRunMarketReport={handleRunMarketReport}
          onMonitorSite={handleMonitorSite}
          onGetAISuggestions={handleGetAISuggestions}
        />

        {/* Projects Section */}
        <ProjectsList
          projects={projects}
          isLoading={isLoading}
          error={error}
          onAddProject={() => setShowAddProjectModal(true)}
          onFindLocation={handleFindLocation}
          onViewAnalysis={handleViewAnalysis}
          onRunAgain={runProjectAnalysis}
          onDelete={deleteProject}
        />
        
        {/* Add Project Modal */}
        {showAddProjectModal && (
          <AddProjectModal
            onClose={handleCloseModal}
            onAdd={handleAddProject}
          />
        )}
        
        {/* Share Project Modal */}
        {shareProjectId && (
          <ShareProjectModal
            projectId={shareProjectId}
            projectName={shareProjectName}
            onClose={handleCloseShareModal}
            onSuccess={refreshProjects}
          />
        )}
      </div>
    </div>
  );
};

export default WelcomePage;
